import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import Spinner from 'common/Spinner';
import isIE11 from 'utils/isIE11';
import ErrorMsg from 'common/ErrorMsg';
import FlyupLabel from 'common/FlyupLabel';
import Tooltip from 'common/Tooltip';
import { withFieldProps } from 'hooks/useFieldProps';
import { v1 as uuid } from 'uuid';

import { connect } from 'react-redux';
import Help from 'common/Help';
import rootStyles from './styles/root';
import caretDown from './icons/caretDown.svg';
import inputStyles from './styles/input';

const styles = ({ forms, palette, spacing, typography }) => ({
  icon: ({ tooltip }) => ({
    background: isIE11 ? `transparent url(${caretDown}) 0 50% no-repeat` : palette.icon,
    color: palette.icon,
    float: 'right',
    height: 32,
    margin: [-35, tooltip ? 16 : 0, 0, 0],
    maskImage: isIE11 ? 'none' : `url(${caretDown})`,
    pointerEvents: 'none',
    position: 'relative',
    width: 32,
    zIndex: 2,
  }),
  root: rootStyles({ spacing }),
  select: {
    ...inputStyles({ forms, palette, typography }),
    ...(isIE11 && {
      '&::-ms-expand': {
        display: 'none',
      },
    }),
    paddingRight: 45,
  },
});

const Lookup = ({
  classes,
  displayOrder,
  error,
  fieldDomId,
  help,
  hideFieldDescr,
  hideFieldLabel,
  hideHelpText,
  loading,
  lookupFieldMap,
  name,
  onBlur,
  onChange,
  onFocus,
  options,
  placeholder,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const lookupOrder = displayOrder === undefined ? Object.keys(options) : displayOrder;
  const inputPurpose = useMemo(() => {
    if (name.includes('suffix')) {
      return 'honorific-suffix';
    }
    if (name.includes('Addr_state') || name.includes('addr_state')) {
      return 'address-level1';
    }
    if (name.includes('country')) {
      return 'country';
    }
    return uuid();
  }, [name]);

  return (
    <>
      <div className={classnames('form-group', { error, loading }, classes.root)}>
        <Spinner loading={loading} />
        <FlyupLabel
          fieldName={name}
          error={error}
          value={value}
          htmlFor={fieldDomId}
          required={required}
          tooltip={tooltip}
          hasIcon
          hideFieldDescr={hideFieldDescr}
          hideFieldLabel={hideFieldLabel}
        />
        <select
          name={name}
          id={fieldDomId}
          value={value}
          aria-describedby={
            classnames({
              [`help-${name}`]: help,
              [`error-${name}`]: error,
            }) || undefined
          }
          aria-invalid={error}
          className={classnames('form-control', classes.select)}
          disabled={readOnly}
          required={required}
          onChange={(e) => {
            onChange(name, e.target.value);
            validate(e.target.value !== value);
          }}
          onBlur={() => {
            onBlur();
            validate(false);
          }}
          onFocus={onFocus}
          autoComplete={inputPurpose}
        >
          <option value="" disabled={required}>
            {placeholder || ''}
          </option>
          {lookupOrder.map(
            (key) =>
              (!(lookupFieldMap?.[key]?.[1] === 'false') || key === value) && (
                <option key={key} value={key}>
                  {options[key]}
                </option>
              ),
          )}
        </select>
        <i className={classnames('handle icon-caret-down', classes.icon)} aria-hidden="true" />
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        {hideHelpText || <Help help={help} id={`help-${name}`} />}
      </div>
      <ErrorMsg name={name} />
    </>
  );
};

Lookup.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  displayOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  hideFieldDescr: PropTypes.bool,
  hideFieldLabel: PropTypes.bool,
  hideHelpText: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  lookupFieldMap: PropTypes.objectOf(PropTypes.array),
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  options: PropTypes.objectOf(PropTypes.string).isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

Lookup.defaultProps = {
  hideFieldDescr: undefined,
  hideFieldLabel: undefined,
  hideHelpText: undefined,
  lookupFieldMap: undefined,
};

const LookupStyled = withStyles(styles)(Lookup);

const mapStateToProps = (
  { fields },
  { hideFieldDescr, hideFieldLabel, hideHelpText, lookupFieldMap, lookupOrder, name, options },
) => ({
  displayOrder: lookupOrder || fields[name]?.lookupOrder || Object.keys(fields[name]?.lookupValues),
  hideFieldDescr,
  hideFieldLabel,
  hideHelpText,
  lookupFieldMap: lookupFieldMap || fields[name]?.lookupFieldMap,
  options: options || fields[name]?.lookupValues,
});

const LookupWithConnect = connect(mapStateToProps)(LookupStyled);
export { LookupStyled as LookupBase, LookupWithConnect };

export default withFieldProps(mapStateToProps)(LookupStyled);
